import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip, Skeleton, Stack, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { Apartment, FitnessCenter, Groups, Timer } from '@mui/icons-material';
import PillTabs from 'components/shared/tabs/PillTabs';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import OrgExpandableRow from 'components/org-client/OrgExpandableRow';
import OrgStatTile from 'components/org-client/OrgStatTile';
import useOrgPageTitle from 'components/org-client/useOrgPageTitle';
import useLogger from 'hooks/useLogger';
import { bgOf, toneOf } from 'components/admin-organizations/wellbeingTones';
import { MY_ORGANIZATION_USAGE } from 'api/graphql/orgClient';

const WINDOWS = [4, 8, 12];

const formatHours = (minutes) => Math.round((minutes || 0) / 60);

/**
 * Adopción por departamento: cuánto se está usando el beneficio en cada área. Son datos de
 * USO, no de salud: el bienestar por departamento vive en la pantalla de Bienestar y llega
 * con k-anonymity. Aquí tampoco hay nombres, solo agregados.
 */
function OrgClientDepartments() {
  const { t } = useTranslation(['orgClient']);
  const { logError } = useLogger();
  const pageTitle = t('menu.departments', { defaultValue: 'Departamentos' });
  useOrgPageTitle(pageTitle);

  const [windowIndex, setWindowIndex] = useState(0);
  const weeks = WINDOWS[windowIndex];

  const { data, loading, error } = useQuery(MY_ORGANIZATION_USAGE, {
    variables: { weeks },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientDepartments', queryError)
  });

  const usage = useMemo(() => data?.myOrganizationUsage || [], [data]);

  const totals = useMemo(() => {
    const members = usage.reduce((acc, row) => acc + (row.members || 0), 0);
    const active = usage.reduce((acc, row) => acc + (row.active_members || 0), 0);
    const workouts = usage.reduce((acc, row) => acc + (row.workouts || 0), 0);
    const minutes = usage.reduce((acc, row) => acc + (row.minutes || 0), 0);

    return {
      members,
      active,
      workouts,
      minutes,
      adoption: members > 0 ? Math.round((active / members) * 100) : null
    };
  }, [usage]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <SEOPageTitle title={pageTitle} />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 520 }}>
          {t('departments.intro', {
            defaultValue: 'Cuánta gente de cada área está usando el beneficio y con qué constancia.'
          })}
        </Typography>
        <PillTabs
          value={windowIndex}
          onChange={(event, next) => setWindowIndex(next)}
          ariaLabel={t('filters.windowAria', { defaultValue: 'Ventana de tiempo' })}
          tabs={WINDOWS.map((option) => ({
            key: `w-${option}`,
            label: t('filters.weeks', { count: option, defaultValue: '{{count}} semanas' })
          }))}
        />
      </Stack>

      {error && usage.length === 0 && (
        <Alert severity="error" variant="filled" sx={{ borderRadius: 3, mb: 2.5 }}>
          {t('errors.departments', {
            defaultValue: 'No pudimos cargar la adopción por departamento. Intenta de nuevo en un momento.'
          })}
        </Alert>
      )}

      {loading && usage.length === 0 ? (
        <Stack spacing={1}>
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} variant="rounded" height={72} sx={{ borderRadius: '14px' }} />
          ))}
        </Stack>
      ) : (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid xs={12} sm={6} lg={3}>
              <OrgStatTile
                icon={<Groups sx={{ fontSize: 20 }} />}
                label={t('departments.totalMembers', { defaultValue: 'Personas con beneficio' })}
                value={totals.members}
              />
            </Grid>
            <Grid xs={12} sm={6} lg={3}>
              <OrgStatTile
                icon={<Apartment sx={{ fontSize: 20 }} />}
                label={t('departments.adoption', { defaultValue: 'Adopción' })}
                value={totals.adoption ?? '--'}
                unit={totals.adoption === null ? null : '%'}
                hint={t('departments.adoptionHint', {
                  active: totals.active,
                  members: totals.members,
                  defaultValue: '{{active}} de {{members}} entrenaron en la ventana'
                })}
              />
            </Grid>
            <Grid xs={12} sm={6} lg={3}>
              <OrgStatTile
                icon={<FitnessCenter sx={{ fontSize: 20 }} />}
                label={t('departments.workouts', { defaultValue: 'Entrenamientos' })}
                value={totals.workouts}
              />
            </Grid>
            <Grid xs={12} sm={6} lg={3}>
              <OrgStatTile
                icon={<Timer sx={{ fontSize: 20 }} />}
                label={t('departments.hours', { defaultValue: 'Horas de actividad' })}
                value={formatHours(totals.minutes)}
                unit="h"
              />
            </Grid>
          </Grid>

          {usage.length === 0 ? (
            <Box
              sx={{
                p: 4,
                borderRadius: 3,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'hairline.strong',
                backgroundColor: 'surface.overlay'
              }}>
              <Apartment sx={{ color: 'text.disabled', fontSize: 32 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 1 }}>
                {t('departments.emptyTitle', { defaultValue: 'Todavía no hay departamentos' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 460, mx: 'auto' }}>
                {t('departments.emptyBody', {
                  defaultValue:
                    'Cuando el equipo de OTRIX cargue las áreas de tu empresa, vas a ver aquí cuánto usa el beneficio cada una.'
                })}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {usage.map((row) => {
                const adoption = Math.round(row.adoption_percent || 0);

                return (
                  <OrgExpandableRow
                    key={row.department_id || 'none'}
                    title={row.department_name || t('departments.noDepartment', { defaultValue: 'Sin departamento' })}
                    subtitle={t('departments.rowMeta', {
                      active: row.active_members,
                      members: row.members,
                      defaultValue: '{{active}} de {{members}} personas activas'
                    })}
                    trailing={
                      <Chip
                        size="small"
                        label={`${adoption}%`}
                        sx={{
                          flex: 'none',
                          fontWeight: 800,
                          fontVariantNumeric: 'tabular-nums',
                          color: toneOf(adoption),
                          backgroundColor: bgOf(adoption)
                        }}
                      />
                    }>
                    <Grid container spacing={1.5} sx={{ pt: 1 }}>
                      <Grid xs={6} sm={3}>
                        <OrgStatTile
                          label={t('departments.workouts', { defaultValue: 'Entrenamientos' })}
                          value={row.workouts}
                        />
                      </Grid>
                      <Grid xs={6} sm={3}>
                        <OrgStatTile
                          label={t('departments.hours', { defaultValue: 'Horas de actividad' })}
                          value={formatHours(row.minutes)}
                          unit="h"
                        />
                      </Grid>
                      <Grid xs={6} sm={3}>
                        <OrgStatTile
                          label={t('departments.perMember', { defaultValue: 'Por persona' })}
                          value={Number(row.workouts_per_member || 0).toFixed(1)}
                          hint={t('departments.perMemberHint', { defaultValue: 'Entrenamientos por persona' })}
                        />
                      </Grid>
                      <Grid xs={6} sm={3}>
                        <OrgStatTile
                          label={t('departments.completion', { defaultValue: 'Cumplimiento' })}
                          value={row.avg_completion_percent ?? '--'}
                          unit={row.avg_completion_percent === null ? null : '%'}
                          hint={t('departments.completionHint', { defaultValue: 'Promedio de rutina terminada' })}
                        />
                      </Grid>
                    </Grid>
                  </OrgExpandableRow>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}

export default OrgClientDepartments;
